import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { Box, Button } from '@mui/material'
import { Table } from 'shared/ui/Table/Table'
import React, { useMemo } from 'react'
import { TreeStandards } from 'components/tree/standardUtilities'
import {
  bulkReplaceStandards,
  getPotentialChildren,
  getStandardsToReplace,
  replaceStandards,
  StandardAndPotentialChildren,
  StandardReplacement,
  StandardReplacementNoKiddomStandard,
  StandardReplacementNoMapping,
} from './utilities'
import { OpenNodeButton } from 'components/tree_navigation/TreeNavigation'
import { workingIdMapAtom } from '../findAndReplace/atoms'
import { LoadingButton } from '@mui/lab'
import { bulkDeleteLeaves } from '../TreeActions'
import { processInChunks } from 'utility'

export function ReplaceStandardsTable({
  treeStandards,
  standardsReplacementMap,
  originalStandardsMap,
  originalReplacements,
  standardState,
}: {
  treeStandards: TreeStandards
  standardsReplacementMap: Record<string, string[]>
  originalStandardsMap: Record<string, string>
  originalReplacements: Record<string, string[]>
  standardState: string
}) {
  const [replacementsWithNoIssues, setReplacementsWithNoIssues] =
    React.useState<StandardReplacement[]>([])
  const [replacementsWithNoMapping, setReplacementsWithNoMapping] =
    React.useState<StandardReplacementNoMapping[]>([])
  const [
    replacementsWithNoKiddomStandard,
    setReplacementsWithNoKiddomStandard,
  ] = React.useState<StandardReplacementNoKiddomStandard[]>([])
  const [loading, setLoading] = React.useState(true)
  const [updating, setUpdating] = React.useState(false)
  const [updated, setUpdated] = React.useState(false)
  const [updatingNumber, setUpdatingNumber] = React.useState(0)
  const [deleting, setDeleting] = React.useState(false)
  const [deleted, setDeleted] = React.useState(false)
  const noIssuesColumns = useNoIssuesTableColumns()
  const noMappingColumns = useNoMappingTableColumns()
  const noKiddomStandardColumns = useNoKiddomStandardTableColumns()
  const standardReplacementColumns = useStandardReplacementsColumns(
    originalStandardsMap,
    originalReplacements,
  )
  const uniqueStandardsWithNoMappingColumns =
    useUniqueStandardsWithNoMappingColumns()
  const uniqueStandardsWithNoMapping = Array.from(
    new Set(
      replacementsWithNoMapping.map((r) => r.currentStandard.standardName),
    ),
  )
  const uniqueStandardsWithNoMappingAndPotentialChildren: StandardAndPotentialChildren[] =
    uniqueStandardsWithNoMapping.map((s) => {
      return {
        standard: s,
        potentialChildren: getPotentialChildren(s, standardsReplacementMap),
      }
    })

  React.useEffect(() => {
    setLoading(true)
    getStandardsToReplace(
      treeStandards,
      standardsReplacementMap,
      standardState,
    ).then((d) => {
      setLoading(false)
      setReplacementsWithNoIssues(d.standardReplacementsWithNoIssues)
      setReplacementsWithNoMapping(d.standardReplacementsWithNoMapping)
      setReplacementsWithNoKiddomStandard(
        d.standardReplacementsWithNoKiddomStandard,
      )
    })
  }, [treeStandards, standardsReplacementMap, standardState])

  return (
    <Box>
      <Table
        title={'Standards to Replace'}
        items={Object.entries(standardsReplacementMap)}
        columns={standardReplacementColumns}
      />
      <Table
        title={`Replacements With No Issues (${replacementsWithNoIssues.length})`}
        items={replacementsWithNoIssues}
        columns={noIssuesColumns}
        toolBarItems={[
          <LoadingButton
            key="update-button"
            loading={loading || updating}
            disabled={updated}
            onClick={() => {
              setUpdating(true)
              setUpdatingNumber(0)
              processInChunks(replacementsWithNoIssues, 25, (s) => {
                setUpdatingNumber((prev) => prev + 25)
                return bulkReplaceStandards(s)
              }).then(() => {
                setUpdating(false)
                setUpdated(true)
              })
            }}
          >
            {updated ? 'Updated' : 'Update All'}
          </LoadingButton>,
          <progress
            key="update-progress"
            value={updatingNumber / replacementsWithNoIssues.length}
          ></progress>,
        ]}
      />
      <Table
        toolBarItems={[
          <LoadingButton
            key="delete-button"
            loading={loading || deleting}
            disabled={deleted}
            onClick={() => {
              setDeleting(true)
              bulkDeleteLeaves({
                treeId: replacementsWithNoMapping[0].skillLeaf.tree_id!,
                versionId: replacementsWithNoMapping[0].skillLeaf.version!,
                leaves: replacementsWithNoMapping.map((r) => r.skillLeaf.id),
              }).then(() => {
                setDeleting(false)
                setDeleted(true)
              })
            }}
          >
            {deleted ? 'Deleted' : 'Delete All'}
          </LoadingButton>,
        ]}
        title={`Replacements With No Mapping`}
        items={replacementsWithNoMapping}
        columns={noMappingColumns}
      />
      <Table
        toolBarItems={[
          <Button
            key="copy-standards"
            onClick={() => {
              navigator.clipboard.writeText(
                uniqueStandardsWithNoMapping.join('\n'),
              )
            }}
          >
            Copy All Unique Standards
          </Button>,
          <Button
            key="copy-standards-no-children"
            onClick={() => {
              navigator.clipboard.writeText(
                uniqueStandardsWithNoMappingAndPotentialChildren
                  .filter((s) => s.potentialChildren.length === 0)
                  .map((s) => s.standard)
                  .join('\n'),
              )
            }}
          >
            Copy All Unique Standards With No Potential Children
          </Button>,
        ]}
        title={`Unique Standards With No Mapping`}
        items={uniqueStandardsWithNoMappingAndPotentialChildren}
        columns={uniqueStandardsWithNoMappingColumns}
      />
      <Table
        title={`Replacements With No Kiddom Standard`}
        items={replacementsWithNoKiddomStandard}
        columns={noKiddomStandardColumns}
      />
    </Box>
  )
}

function useUniqueStandardsWithNoMappingColumns() {
  return useMemo(() => {
    return [
      {
        title: <strong>Standard</strong>,
        width: 100,
        rowContent(a: StandardAndPotentialChildren) {
          return a.standard
        },
      },
      {
        title: <strong>Potential Children</strong>,
        width: 100,
        rowContent(a: StandardAndPotentialChildren) {
          return (
            <div>
              {a.potentialChildren.map((s, index) => (
                <div key={index}>{s}</div>
              ))}
            </div>
          )
        },
      },
    ]
  }, [])
}

function useStandardReplacementsColumns(
  originalStandardsMap: { [normalizedKey: string]: string },
  originalReplacements: { [normalizedKey: string]: string[] },
) {
  return useMemo(() => {
    return [
      {
        title: <strong>Old Standard</strong>,
        width: 100,
        rowContent(a: [string, string[]]) {
          return originalStandardsMap[a[0]] || a[0]
        },
      },
      {
        title: <strong>New Standard</strong>,
        width: 100,
        rowContent(a: [string, string[]]) {
          const origValues = originalReplacements[a[0]] || a[1]
          return (
            <div>
              {origValues.map((s, index) => (
                <div key={index}>{s}</div>
              ))}
            </div>
          )
        },
      },
    ]
  }, [originalStandardsMap, originalReplacements])
}

function useNoIssuesTableColumns() {
  const idMap = useAtomValue(workingIdMapAtom)
  return useMemo(() => {
    return [
      {
        title: <strong>Leaf ID</strong>,
        width: 100,
        rowContent(a: StandardReplacement) {
          if (idMap && a.skillLeaf.parent_id) {
            return (
              <OpenNodeButton
                node={idMap[a.skillLeaf.parent_id].node}
                idMap={idMap}
              />
            )
          }
          return null
        },
      },
      {
        title: <strong>Current Standard</strong>,
        width: 100,
        rowContent(a: StandardReplacement) {
          return a.currentStandard.standardName
        },
      },
      {
        title: <strong>New Standard</strong>,
        width: 100,
        rowContent(a: StandardReplacement) {
          return (
            <div>
              {a.newStateStandards.map((s, index) => (
                <div key={index}>{s.standardName}</div>
              ))}
            </div>
          )
        },
      },
      {
        title: <strong>Replace/Add</strong>,
        width: 100,
        rowContent(a: StandardReplacement) {
          return (
            <Button
              onClick={() => {
                replaceStandards(a)
              }}
            >
              {'Replace/Add'}
            </Button>
          )
        },
      },
    ]
  }, [])
}

function useNoMappingTableColumns() {
  const idMap = useAtomValue(workingIdMapAtom)
  return useMemo(() => {
    return [
      {
        title: <strong>Leaf ID</strong>,
        width: 100,
        rowContent(a: StandardReplacementNoMapping) {
          if (idMap && a.skillLeaf.parent_id) {
            return (
              <OpenNodeButton
                node={idMap[a.skillLeaf.parent_id].node}
                idMap={idMap}
              />
            )
          }
        },
      },
      {
        title: <strong>Current Standard</strong>,
        width: 100,
        rowContent(a: StandardReplacementNoMapping) {
          return a.currentStandard.standardName
        },
      },
    ]
  }, [])
}

function useNoKiddomStandardTableColumns() {
  const idMap = useAtomValue(workingIdMapAtom)
  return useMemo(() => {
    return [
      {
        title: <strong>Leaf ID</strong>,
        width: 100,
        rowContent(a: StandardReplacementNoKiddomStandard) {
          if (idMap && a.skillLeaf.parent_id) {
            return (
              <OpenNodeButton
                node={idMap[a.skillLeaf.parent_id].node}
                idMap={idMap}
              />
            )
          }
        },
      },
      {
        title: <strong>Current Standard</strong>,
        width: 100,
        rowContent(a: StandardReplacementNoKiddomStandard) {
          return a.currentStandard.standardName
        },
      },
      {
        title: <strong>New Standard</strong>,
        width: 100,
        rowContent(a: StandardReplacementNoKiddomStandard) {
          return a.newStateStandardName.reduce((acc, s) => acc + s + ', ', '')
        },
      },
    ]
  }, [])
}
